import React, { useEffect, useState } from 'react';
import { ref, get, set, onValue, off } from 'firebase/database';
import { rtdb } from '../../../firebase';
import { Clock, CheckCircle2, AlertCircle, Users } from 'lucide-react';

function WaitTimePanel({ crousId }) {
  const [baseMinutes, setBaseMinutes] = useState('');
  const [liveData, setLiveData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const waitRef = ref(rtdb, `wait_times/${crousId}`);
    const unsub = onValue(waitRef, (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        setLiveData(data);
        if (data.base?.minutes != null) {
          setBaseMinutes(String(data.base.minutes));
        }
      } else {
        setLiveData(null);
      }
    });

    return () => off(waitRef, 'value', unsub);
  }, [crousId]);

  const getRecentVotes = () => {
    if (!liveData?.votes) return { count: 0, adjustment: 0 };
    const now = Date.now();
    const WINDOW = 30 * 60 * 1000;
    let count = 0;
    let adjustment = 0;
    Object.values(liveData.votes).forEach((v) => {
      if (now - v.timestamp < WINDOW) {
        count++;
        adjustment += v.vote;
      }
    });
    return { count, adjustment };
  };

  const handleSubmit = async () => {
    const minutes = parseInt(baseMinutes, 10);
    if (isNaN(minutes) || minutes < 1 || minutes > 60) {
      setFeedback({ kind: 'err', text: 'Entre un temps entre 1 et 60 minutes.' });
      return;
    }

    setSubmitting(true);
    setFeedback(null);
    try {
      const baseRef = ref(rtdb, `wait_times/${crousId}/base`);
      await set(baseRef, { minutes, updatedAt: Date.now() });
      setFeedback({ kind: 'ok', text: `Temps de base défini à ${minutes} min. Les votes étudiants ajusteront en temps réel.` });
    } catch (err) {
      setFeedback({ kind: 'err', text: err.message || 'Erreur.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = async () => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const baseRef = ref(rtdb, `wait_times/${crousId}/base`);
      await set(baseRef, null);
      setBaseMinutes('');
      setFeedback({ kind: 'ok', text: 'Temps de base supprimé. L\'estimation automatique reprend.' });
    } catch (err) {
      setFeedback({ kind: 'err', text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const { count: voteCount, adjustment: voteAdj } = getRecentVotes();
  const hasBase = liveData?.base?.minutes != null;
  const effectiveMinutes = hasBase
    ? Math.max(1, liveData.base.minutes + voteAdj * 3)
    : null;

  return (
    <div className="est-card">
      <div className="est-card-head">
        <div>
          <span className="est-kicker">Temps réel</span>
          <h2>Temps d'attente</h2>
          <p className="est-muted">
            Définis un temps d'attente de base. Les étudiants ajustent en votant
            « Plus rapide » ou « Plus long » (±3 min par vote, fenêtre 30 min).
          </p>
        </div>
        {hasBase && (
          <div className="est-badge est-badge-active">
            Base : {liveData.base.minutes} min
          </div>
        )}
      </div>

      {/* Live stats */}
      {(hasBase || voteCount > 0) && (
        <div className="est-kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="est-kpi">
            <span>Base CROUS</span>
            <strong>{hasBase ? `${liveData.base.minutes}` : '—'}</strong>
            <small>minutes</small>
          </div>
          <div className="est-kpi">
            <span>Votes actifs</span>
            <strong>{voteCount}</strong>
            <small>{voteAdj > 0 ? `+${voteAdj * 3} min` : voteAdj < 0 ? `${voteAdj * 3} min` : 'neutre'}</small>
          </div>
          <div className="est-kpi">
            <span>Affiché aux étudiants</span>
            <strong>{effectiveMinutes != null ? `~${Math.round(effectiveMinutes)}` : 'auto'}</strong>
            <small>minutes</small>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="est-wait-input-row">
        <label className="est-field" style={{ flex: 1, maxWidth: 200 }}>
          <span>Temps d'attente de base (min)</span>
          <div className="est-input-wrap">
            <Clock size={16} />
            <input
              type="number"
              min="1"
              max="60"
              value={baseMinutes}
              onChange={(e) => setBaseMinutes(e.target.value)}
              placeholder="ex: 12"
            />
          </div>
        </label>
        <p className="est-hint" style={{ maxWidth: 400 }}>
          <Users size={14} />
          Les étudiants voient ce temps ajusté par les votes communautaires en temps réel.
        </p>
      </div>

      {feedback && (
        <div className={`est-alert ${feedback.kind === 'ok' ? 'est-alert-ok' : 'est-alert-error'}`}>
          {feedback.kind === 'ok' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{feedback.text}</span>
        </div>
      )}

      <div className="est-actions">
        <button
          type="button"
          className="est-btn est-btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'En cours...' : 'Définir le temps de base'}
        </button>
        {hasBase && (
          <button
            type="button"
            className="est-btn est-btn-ghost"
            onClick={handleClear}
            disabled={submitting}
          >
            Supprimer (estimation auto)
          </button>
        )}
      </div>
    </div>
  );
}

export default WaitTimePanel;
